/**
 * DOMMatrix shim — legacy (nomodule) bundle ONLY.
 *
 * WHY THIS EXISTS
 * Android 7.0's WebView is Chrome 53; DOMMatrix landed in Chrome 61. pdf.js
 * evaluates a bare `new DOMMatrix()` at MODULE scope, so merely importing it
 * throws `ReferenceError: DOMMatrix is not defined` and kills the app before
 * Vue mounts — a blank white page, the exact symptom ANSOR's drivers
 * reported. @vitejs/plugin-legacy fixes the SYNTAX gap and does nothing for
 * this API gap, so the legacy bundle ran and then died anyway. Measured on
 * the cs7_0 emulator 2026-09-18.
 *
 * WHY pdf.js IS EVEN HERE
 * @claimshield/shared's barrel re-exports SignDocumentCanvas, whose
 * module-scope `GlobalWorkerOptions.workerSrc = ...` assignment makes the
 * module unshakeable by tree-shaking. This app never renders that component.
 * Dropping it from the entry chunk is the better fix (it would also cut a
 * large chunk off a public form on driver phones) but it touches the
 * portal/ops e-signature path, so it is proposed separately rather than
 * cascaded into this one.
 *
 * WHY IT DELEGATES INSTEAD OF IMPLEMENTING
 * Chrome 53 ships WebKitCSSMatrix, which has correct affine math - probed
 * on-device 2026-09-18: multiply and inverse both present and numerically
 * correct. This wraps that rather than reimplementing matrix math, so there
 * is no silently-wrong-arithmetic failure mode. What WebKitCSSMatrix lacks
 * is only the MUTABLE DOMMatrix methods (invertSelf, multiplySelf,
 * preMultiplySelf), which are added here on top of the native immutable ones.
 *
 * TWO HARD CONSTRAINTS - do not "clean these up"
 *  1. PLAIN .js, ES5 ONLY. additionalLegacyPolyfills is built by a raw
 *     Rollup pass with NO TypeScript transform and NO down-levelling. A .ts
 *     file fails with "Expected a semicolon"; any modern syntax (?., ??,
 *     const, arrow) fails at runtime with "Unexpected token ?" and then
 *     "System is not defined", because the syntax error aborts SystemJS.
 *     Both were observed on-device before this file was written this way.
 *  2. No imports. A dependency here ships its own modern syntax into the
 *     same untranspiled chunk and reintroduces (1).
 *
 * Ships in polyfills-legacy only, so modern browsers pay nothing.
 */
;(function () {
  if (typeof window === 'undefined') return
  if (typeof window.DOMMatrix !== 'undefined') return

  var Native = window.WebKitCSSMatrix
  if (!Native) return

  function M(init) {
    this.a = 1
    this.b = 0
    this.c = 0
    this.d = 1
    this.e = 0
    this.f = 0
    if (init && typeof init.length === 'number' && init.length >= 6) {
      this.a = init[0]
      this.b = init[1]
      this.c = init[2]
      this.d = init[3]
      this.e = init[4]
      this.f = init[5]
    } else if (typeof init === 'string' && init !== '') {
      copy(this, new Native(init))
    } else if (init && typeof init.a === 'number') {
      copy(this, init)
    }
  }

  function copy(dst, src) {
    dst.a = src.a
    dst.b = src.b
    dst.c = src.c
    dst.d = src.d
    dst.e = src.e
    dst.f = src.f
    return dst
  }

  function toNative(m) {
    return copy(new Native(), m)
  }

  function wrap(n) {
    return new M([n.a, n.b, n.c, n.d, n.e, n.f])
  }

  M.prototype.multiply = function (o) {
    return wrap(toNative(this).multiply(toNative(o)))
  }
  M.prototype.multiplySelf = function (o) {
    return copy(this, toNative(this).multiply(toNative(o)))
  }
  M.prototype.preMultiplySelf = function (o) {
    return copy(this, toNative(o).multiply(toNative(this)))
  }
  M.prototype.inverse = function () {
    return wrap(toNative(this).inverse())
  }
  M.prototype.invertSelf = function () {
    return copy(this, toNative(this).inverse())
  }
  M.prototype.translate = function (x, y) {
    return wrap(toNative(this).translate(x || 0, y || 0, 0))
  }
  M.prototype.translateSelf = function (x, y) {
    return copy(this, toNative(this).translate(x || 0, y || 0, 0))
  }
  M.prototype.scale = function (sx, sy) {
    var y = sy === undefined || sy === null ? sx : sy
    return wrap(toNative(this).scale(sx, y, 1))
  }
  M.prototype.scaleSelf = function (sx, sy) {
    var y = sy === undefined || sy === null ? sx : sy
    return copy(this, toNative(this).scale(sx, y, 1))
  }
  M.prototype.toString = function () {
    return (
      'matrix(' + this.a + ', ' + this.b + ', ' + this.c + ', ' +
      this.d + ', ' + this.e + ', ' + this.f + ')'
    )
  }

  window.DOMMatrix = M
  if (typeof window.DOMMatrixReadOnly === 'undefined') window.DOMMatrixReadOnly = M
})()
